import ModalV2 from '@/components/ModalV2';
import Page from '@/components/page/Page';
import IconV2 from '@/components/v2/IconV2';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import Tabs from '@/components/Tabs';
import AppPicker from '@/components/AppPicker';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import sendToast from '@/lib/sendToast';
import useCredentialMapping from '@/hooks/useCredentialMapping';
import { FiExternalLink, FiPlus, FiPackage } from '@/components/react-icons/main/index.esm';
import { Link } from 'react-router-dom';
import Button from '@/components/Button';
import Spinner from '@/components/Spinner';
import EmptyStateNotice from '@/components/EmptyStateNotice';
import selectors from '@/redux/selectors/selectors';
import credentialActions from '@/redux/actions/credentialActions';
import authTypesMap from '@shared/schema/authTypesMap';
import { CREDENTIAL_LABEL_SOURCE_MANUAL } from '@shared/schema/credentialLabelSource.js';
import { FiSearch } from 'react-icons/fi';
import CredentialStatusIcon from '@/screens/credentials/components/CredentialStatusIcon';
import Tag from '@/components/Tag';
import formatDate from '@/screens/credentials/lib/formatDate';
import getUsage from '../lib/getUsage';
import projectActions from '@/screens/workflows/redux/actions/projectActions';
import { LuUnlink } from 'react-icons/lu';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import { getIntegrationsProject } from '@/redux/actions/gravityActions';

const ProjectAppsModal = () => {
  const modalRef = useRef();
  const appPickerRef = useRef();
  const lastFetchedProjectIdRef = useRef(null);
  const modalState = useSelector((state) => state?.ui?.modals?.projectApps);
  const projects = useSelector((state) => state?.settings?.integrationsProjects) || [];
  const loadingProject = useArgSelector(selectors.isLoading, 'getIntegrationsProject');
  const show = modalState?.open || false;
  const modalProject = modalState?.project || null;
  const modalProjectId = modalProject?.id || modalProject?.projectId || null;
  const project = projects.find((p) => p.id === modalProjectId) || {};
  const projectId = project?.id;
  const projectApps = project?.apps || [];
  const companyId = project?.companyId;
  const [removingApps, setRemovingApps] = useState({});
  const { isLoading, credentialMap } = useCredentialMapping(companyId);
  const credentials = useArgSelector(selectors.credentialsByCompany, companyId) || [];
  const appsMetadata = useSelector(selectors.appsMetadata) || [];

  useEffect(() => {
    if (!show) {
      lastFetchedProjectIdRef.current = null;
    }
  }, [show]);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (!modalProjectId) {
      return;
    }

    if (projectId) {
      return;
    }

    if (loadingProject) {
      return;
    }

    if (lastFetchedProjectIdRef.current === modalProjectId) {
      return;
    }

    lastFetchedProjectIdRef.current = modalProjectId;
    getIntegrationsProject({ projectId: modalProjectId });
  }, [show, modalProjectId, projectId, loadingProject]);

  // Right pane state
  const [activeTab, setActiveTab] = useState('add-new');
  const [selectedApp, setSelectedApp] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [autoGeneratedSearch, setAutoGeneratedSearch] = useState('');
  const [newType, setNewType] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [existingSearch, setExistingSearch] = useState('');
  const [appPickerSearch, setAppPickerSearchInternal] = useState('');

  const setAppPickerSearch = useCallback(
    (value) => {
      setAppPickerSearchInternal(value);

      // If user is changing away from auto-generated search, clear selected app
      if (selectedApp && autoGeneratedSearch && value !== autoGeneratedSearch) {
        setSelectedApp(null);
        setAutoGeneratedSearch('');
        setNewLabel('');
        setNewType('');
      }
    },
    [selectedApp, autoGeneratedSearch]
  );

  const handleClose = () => {
    setModalState('projectApps', { open: false });
    // Reset state when closing
    setSelectedApp(null);
    setNewLabel('');
    setAutoGeneratedSearch('');
    setNewType('');
    setAppPickerSearchInternal('');
  };

  const handleRemoveApp = async (credentialId) => {
    if (!projectId) {
      return;
    }

    const hasApp = projectApps.some((app) => app?.credentialId === credentialId);
    if (!hasApp) {
      return;
    }

    setRemovingApps((prev) => ({ ...prev, [credentialId]: true }));
    const nextApps = projectApps.filter((app) => app?.credentialId !== credentialId);

    try {
      await projectActions.set('apps', nextApps, { projectId });

      sendToast({
        type: 'success',
        message: 'App successfully removed from project'
      });
    } catch (error) {
      console.error('Failed to remove app from project', error);
      sendToast({
        type: 'error',
        message: 'Failed to remove app from project'
      });
    } finally {
      setRemovingApps((prev) => {
        const updated = { ...prev };
        delete updated[credentialId];
        return updated;
      });
    }
  };

  const handleAddApp = async (credentialId) => {
    if (!projectId || !credentialId) {
      return null;
    }

    const currentApps = projectApps;
    const alreadyAdded = currentApps.some((app) => app.credentialId === credentialId);

    if (alreadyAdded) {
      return currentApps;
    }

    const nextApps = [...currentApps, { credentialId, timestamp: new Date().toISOString() }];

    try {
      await projectActions.set('apps', nextApps, { projectId });

      sendToast({
        type: 'success',
        message: 'App successfully added to project'
      });
      return nextApps;
    } catch (error) {
      console.error('Failed to add app to project', error);
      sendToast({
        type: 'error',
        message: 'Failed to add app to project'
      });
      throw error;
    }
  };

  const matchedCredentials = useMemo(() => {
    return credentialMap({ items: projectApps });
  }, [projectApps, credentialMap]);

  const existingProjectCredentialIds = useMemo(() => {
    return projectApps.map((n) => n?.credentialId);
  }, [projectApps]);

  const addExistingCandidates = useMemo(() => {
    return (credentials || []).filter((cred) => cred.companyId === companyId && !existingProjectCredentialIds.includes(cred.credentialId));
  }, [credentials, companyId, existingProjectCredentialIds]);

  const mappedExistingCandidates = useMemo(() => {
    const items = credentialMap({ items: addExistingCandidates }).sort((a, b) => {
      const aLastUsed = new Date(a.credential.testedOn || a.credential.updatedOn).getTime();
      const bLastUsed = new Date(b.credential.testedOn || b.credential.updatedOn).getTime();

      return bLastUsed - aLastUsed;
    });
    if (!existingSearch) {
      return items;
    }
    const query = existingSearch?.toLowerCase();
    return items.filter((n) => n?.label?.toLowerCase()?.includes(query) || n?.appName?.toLowerCase()?.includes(query));
  }, [addExistingCandidates, credentialMap, existingSearch]);

  const getAppMetadataById = useCallback((appId) => appsMetadata?.find((a) => a?.id === appId), [appsMetadata]);

  const handleCreateCredential = useCallback(
    async ({ appId, label, type }) => {
      setIsCreating(true);
      try {
        const credentialId = await credentialActions.set({
          companyId,
          appId,
          secrets: { type, label },
          meta: { type, label, labelSource: CREDENTIAL_LABEL_SOURCE_MANUAL }
        });

        await handleAddApp(credentialId);
        setSelectedApp(null);
        setNewLabel('');
        setAutoGeneratedSearch('');
        setNewType('');
        setAppPickerSearchInternal('');

        setTimeout(() => {
          if (appPickerRef.current) {
            appPickerRef.current.focus();
          }
        }, 100);
      } finally {
        setIsCreating(false);
      }
    },
    [companyId, handleAddApp]
  );

  const handleAppSelect = (app) => {
    const meta = getAppMetadataById(app?.id) || {};
    const authTypes = meta?.authTypes || [];

    // Calculate existing credential count for this app
    const existingCount = (project?.apps || []).filter((projectApp) => {
      const cred = credentials.find((c) => c.credentialId === projectApp.credentialId);
      return cred?.appId === app?.id;
    }).length;

    // Default new state
    const appName = app?.name || '';
    setSelectedApp(app);
    setNewType(authTypes?.[0] || '');
    setNewLabel(existingCount === 0 ? appName : `${appName} ${existingCount + 1}`);
    setAutoGeneratedSearch(appName);
    setAppPickerSearchInternal(appName);
  };

  const newTypeOptions = useMemo(() => {
    if (!selectedApp) {
      return [];
    }
    const meta = getAppMetadataById(selectedApp?.id) || {};
    const authTypes = meta?.authTypes || [];
    return (authTypes || []).map((t) => ({ label: authTypesMap?.[t] || t, value: t }));
  }, [selectedApp, getAppMetadataById]);

  const shouldShowTypeSelector = useMemo(() => {
    return (newTypeOptions || [])?.length > 1;
  }, [newTypeOptions]);

  const isCreateDisabled = useMemo(() => {
    if (!selectedApp || !newType || !newLabel) {
      return true;
    }
    return false;
  }, [selectedApp, newType, newLabel]);

  const hasApps = matchedCredentials.length > 0;
  const shouldShowLoadingState = !hasApps && isLoading;
  const shouldShowEmptyState = !hasApps && !isLoading;
  const hasExistingCandidates = mappedExistingCandidates.length > 0;
  const existingEmptyStateTitle = existingSearch && hasExistingCandidates ? 'No credentials match your search' : 'No credentials available';
  const existingEmptyStateDescription = existingSearch && hasExistingCandidates ? 'Try another search term or clear it to see all options.' : 'Create a new credential to add to this project.';

  const getModalActions = () => {
    const baseActions = [
      {
        children: 'Close',
        onClick: handleClose,
        className: 'primary'
      }
    ];

    if (activeTab === 'add-new') {
      return [
        {
          icon: FiPlus,
          children: 'Create',
          onClick: () => handleCreateCredential({ appId: selectedApp?.id, label: newLabel || selectedApp?.name, type: newType }),
          className: 'primary',
          disabled: isCreateDisabled,
          loading: isCreating
        }
      ];
    }

    return baseActions;
  };

  return (
    <>
      <ModalV2 //
        show={show}
        onHide={handleClose}
        maxWidth={'1200px'}
        centered={true}
        title={
          <div className="project-apps-modal-title">
            <span>
              Project Apps - {project?.name} ({project?.companyName})
            </span>
          </div>
        }
        modalRef={modalRef}
        className="project-apps-modal project-apps-table-grid"
        actions={getModalActions()}>
        <Page.Section variant="modal" ref={modalRef}>
          <div className="project-apps-content">
            <div className="left-pane">
              {shouldShowLoadingState && (
                <div className="body-loading">
                  <IconV2 icon={Spinner} />
                  <span>Loading credentials</span>
                </div>
              )}
              <ScrollContainerV2>
                {hasApps && (
                  <div className="table-header">
                    <span />
                    <span>Name</span>
                    <span>App</span>
                    <span>Actions</span>
                  </div>
                )}
                {hasApps ? (
                  matchedCredentials.map((credential) => {
                    const isRemoving = removingApps[credential.id] || false;
                    return (
                      <div className={'table-row'} key={credential.id}>
                        <CredentialStatusIcon credential={credential?.credential} />
                        <div className="credential-name">{credential.label}</div>
                        <div className="app-name">
                          <IconV2 icon={credential.icon} title={credential.appName} />
                          {credential.appName}
                        </div>
                        <div className="actions">
                          <Link to={`/credentials/${credential.id}`} target="_blank" rel="noopener noreferrer">
                            <div role="button" className="action-button" data-title="View">
                              <IconV2 icon={FiExternalLink} />
                            </div>
                          </Link>
                          <div role="button" onClick={() => handleRemoveApp(credential.id)} className="action-button" data-title="Unlink" style={{ opacity: isRemoving ? 0.5 : 1, cursor: isRemoving ? 'not-allowed' : 'pointer' }}>
                            <IconV2 icon={<IconV2 icon={LuUnlink} />} />
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : shouldShowEmptyState ? (
                  <EmptyStateNotice title="No apps added yet" description="Start adding apps this project will require." icon={FiPackage} size="sm" />
                ) : null}
              </ScrollContainerV2>
            </div>
            <div className="right-pane">
              <Tabs
                items={[
                  {
                    key: 'add-new',
                    label: 'Add new',
                    children: (
                      <div className="add-new-pane">
                        <AppPicker ref={appPickerRef} onAppSelect={handleAppSelect} showRequestModal={false} selectedApps={selectedApp ? [selectedApp] : []} search={appPickerSearch} onSearchChange={setAppPickerSearch} />
                        {selectedApp && (
                          <div className="new-credential-form">
                            <Input label={'Label'} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder={`${selectedApp?.name} Credential`} autoFocus />
                            {shouldShowTypeSelector && <Select label="Authentication Type" options={newTypeOptions} value={authTypesMap?.[newType] || newType} onChange={(e) => setNewType(e?.value)} useSearch={newTypeOptions?.length > 6} floatingSelectV2Props={{ nullable: false }} />}
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    key: 'add-existing',
                    label: 'Add existing',
                    children: (
                      <div className="add-existing-pane">
                        <Input icon={FiSearch} className="existing-search" placeholder="Search credentials" value={existingSearch} onChange={(e) => setExistingSearch(e.target.value)} />
                        <ScrollContainerV2>
                          {hasExistingCandidates ? (
                            <div className="existing-credentials-list">
                              {mappedExistingCandidates.map((cred) => {
                                const date = formatDate(cred.credential.testedOn || cred.credential.updatedOn);
                                const usage = getUsage(cred.credential);
                                return (
                                  <div key={cred.id} className="existing-credential-row">
                                    <div className="existing-credential-name">
                                      <IconV2 icon={cred.icon} title={cred.appName} />
                                      <span className="label">{cred.label}</span>
                                    </div>
                                    <div className="existing-credential-actions">
                                      {+cred.credential?.workflowCount > 0 && <Tag data-title={usage.title}>{usage.label}</Tag>}
                                      <div className={'description'} data-title={date.title}>
                                        {date.label}
                                      </div>
                                      <CredentialStatusIcon credential={cred.credential} />
                                      <Button data-title="Add credential" className="outline" size="sm" icon={FiPlus} onClick={() => handleAddApp(cred.id)} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <EmptyStateNotice title={existingEmptyStateTitle} description={existingEmptyStateDescription} icon={FiPackage} size="sm" />
                          )}
                        </ScrollContainerV2>
                      </div>
                    )
                  }
                ]}
                defaultActiveKey={activeTab}
                onTabClick={(_, key) => {
                  setActiveTab(key);
                  // Reset add-new tab state when switching away
                  if (key !== 'add-new') {
                    setSelectedApp(null);
                    setNewLabel('');
                    setAutoGeneratedSearch('');
                    setNewType('');
                    setAppPickerSearchInternal('');
                  }
                }}
              />
            </div>
          </div>
        </Page.Section>
      </ModalV2>
    </>
  );
};
export default ProjectAppsModal;
